import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MAX_SUBJECTS,
  type Subject,
  type UsageFilters,
} from './usage-analytics.api';
import { localDay } from './usage-format';

/**
 * The report's state, kept in the URL.
 *
 * A comparison takes a minute to assemble — a couple of people looked up by
 * email, a group filtered down — and in component state it was gone on the
 * first refresh. In the address bar it survives one, and the link can be sent
 * to somebody else to look at the same thing.
 *
 * Only what differs from the default is written, so a page nobody has touched
 * keeps a clean address and keeps meaning "the last thirty days" tomorrow
 * rather than freezing on the dates it was first opened with.
 */

const DEFAULT_RANGE_DAYS = 30;

const DEFAULT_SUBJECTS: Subject[] = [
  { kind: 'group', key: 'everyone', slot: 0 },
];

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (DEFAULT_RANGE_DAYS - 1));
  return { start: localDay(start), end: localDay(end) };
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Subjects from the address bar, which is to say from anyone.
 *
 * Checked field by field rather than trusted: a hand-edited or truncated link
 * should open on what could be understood, not throw in the middle of a render.
 * Slots are checked for collisions too, since two subjects sharing a colour is
 * exactly the confusion the slots exist to prevent.
 */
function parseSubjects(raw: string | null): Subject[] | null {
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;

  const subjects: Subject[] = [];
  const slots = new Set<number>();
  const keys = new Set<string>();

  for (const entry of parsed.slice(0, MAX_SUBJECTS)) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;

    const { key, slot } = item;
    if (typeof key !== 'string' || !key || keys.has(key)) continue;
    if (
      typeof slot !== 'number' ||
      !Number.isInteger(slot) ||
      slot < 0 ||
      slot >= MAX_SUBJECTS ||
      slots.has(slot)
    ) {
      continue;
    }

    const text = (value: unknown) =>
      typeof value === 'string' && value ? value : undefined;
    const whole = (value: unknown) =>
      typeof value === 'number' && Number.isInteger(value) ? value : undefined;
    const day = (value: unknown) =>
      typeof value === 'string' && DAY.test(value) ? value : undefined;

    if (item.kind === 'person') {
      const userId = text(item.userId);
      if (!userId) continue;
      subjects.push({
        kind: 'person',
        key,
        slot,
        userId,
        name: text(item.name) ?? '',
        email: text(item.email) ?? null,
      });
    } else if (item.kind === 'group') {
      subjects.push({
        kind: 'group',
        key,
        slot,
        gender: whole(item.gender),
        ageMin: whole(item.ageMin),
        ageMax: whole(item.ageMax),
        language: whole(item.language),
        guest: typeof item.guest === 'boolean' ? item.guest : undefined,
        role: text(item.role),
        favoriteStoreId: text(item.favoriteStoreId),
        favoriteStoreName: text(item.favoriteStoreName),
        registeredFrom: day(item.registeredFrom),
        registeredTo: day(item.registeredTo),
      });
    } else {
      continue;
    }

    keys.add(key);
    slots.add(slot);
  }

  return subjects;
}

export function useUsageState() {
  const [params, setParams] = useSearchParams();
  // Today's default, fixed for the life of the page so the range does not
  // move under a reader who leaves the tab open past midnight.
  const fallback = useMemo(defaultRange, []);

  const start = DAY.test(params.get('from') ?? '')
    ? (params.get('from') as string)
    : fallback.start;
  const end = DAY.test(params.get('to') ?? '')
    ? (params.get('to') as string)
    : fallback.end;
  const domain = params.get('app') || null;

  const rawSubjects = params.get('who');
  const subjects = useMemo(
    () => parseSubjects(rawSubjects) ?? DEFAULT_SUBJECTS,
    [rawSubjects],
  );

  const filters: UsageFilters = useMemo(
    () => ({ start, end, domain }),
    [start, end, domain],
  );

  const set = (name: string, value: string | null) => {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (value === null) updated.delete(name);
        else updated.set(name, value);
        return updated;
      },
      // A filter change is not a place to go back to: Back should leave the
      // report, not replay every select the reader touched on the way.
      { replace: true },
    );
  };

  return {
    filters,
    subjects,
    defaultRange: fallback,
    /** Everything above as one string, which is all a saved view is. */
    search: params.toString(),
    /**
     * Replaces the whole state with a saved one. A history entry of its own,
     * unlike a filter change: opening another comparison is somewhere a reader
     * may well want to come back from.
     */
    open: (search: string) => setParams(new URLSearchParams(search)),
    setStart: (value: string) =>
      set('from', value && value !== fallback.start ? value : null),
    setEnd: (value: string) =>
      set('to', value && value !== fallback.end ? value : null),
    setDomain: (value: string | null) => set('app', value),
    setSubjects: (next: Subject[]) =>
      set(
        'who',
        JSON.stringify(next) === JSON.stringify(DEFAULT_SUBJECTS)
          ? null
          : JSON.stringify(next),
      ),
  };
}
